<template>
  <div class="detailed-description">
    <slot name="header"></slot>

    <div class="section">
      <h2>{{vul.category}}</h2>
    </div>

    <div class="info-container">
      <div class="info-box">
        <p><strong>{{ $t('vulnerabilityInformation.id') }}</strong> <el-tooltip
            effect="dark"
            :content="vul.vulId"
          ><span class="vulLabel">{{ vul.vulId }}</span></el-tooltip> </p>
        <p><strong>{{ $t('vulnerabilityInformation.cweId') }}</strong> <span class="vulLabel">{{ vul.cweId }}</span></p>
        <p><strong>{{ $t('vulnerabilityInformation.severity') }}</strong> <span class="vulLabel"><severity-tag
              v-if="vul.severity"
              :severity="vul.severity"
            /></span></p>
      </div>
      <div class="info-box">
        <p><strong>{{ vulType === 'detail' ? $t('vulnerabilityInformation.status') : $t('vulnerabilityInformation.payment') }}</strong> <span
            class="vulLabel"
            :class="{'paid': vulStatus, 'unpaid': !vulStatus}"
          >{{ vulStatus ? $t('vulnerabilityInformation.paid') : $t('vulnerabilityInformation.unpaid') }}</span></p>
        <p><strong>{{ $t('vulnerabilityInformation.category') }}</strong> <span class="vulLabel">{{ vul.category }}</span></p>
        <p><strong>{{ $t('vulnerabilityInformation.exploitability') }}</strong> <span class="vulLabel">{{ vul.verifyResult === 'VERIFY_PASS' ? 'POC Confirmed' : '-' }}</span></p>
      </div>
    </div>

    <div class="sections">
      <div class="des-title">
        <h3>{{ $t('vulnerabilityInformation.description') }}</h3>
      </div>
      <p>{{ vul.description }}</p>
    </div>

    <template v-if="sinkInfo?.info?.length">
      <section class="vul-section">
        <component
          :is="vulComponent"
          :vul="vul"
          :sink-info="sinkInfo"
          :source-info="sourceInfo"
          :images="images"
          :language="language"
        />
      </section>
    </template>
    <template v-else>
      <no-data />
    </template>
  </div>
</template>

<script>
import SinkVul from './sink-vul.vue';
import IdorVul from './idor-vul.vue';
import GqlVul from './gql-vul.vue';
import NoData from './no-data.vue';
import CsrfVul from './csrf.vue';
import SeverityTag from '@/components/severity-tag';
import { LANGUAGE_TYPES } from '@/enum';
import { copyText } from '@/utils/copy-text';

const KeyMap = {
  file: 'Class',
  method: 'Method',
  parameter: 'Parameter',
};

export default {
  name: 'VulInfo',
  components: {
    SinkVul,
    IdorVul,
    GqlVul,
    NoData,
    CsrfVul,
    SeverityTag,
  },
  props: {
    vul: {
      type: Object,
      default: () => {},
    },
    vulType: {
      type: String,
      default: 'detail',
    },
  },
  computed: {
    vulStatus() {
      const { vul } = this;
      const paid = Boolean(vul?.sink);

      return paid;
    },
    vulComponent() {
      const { category, vulId } = this.vul;

      // 根据漏洞类型返回对应组件
      if (category === 'Incorrect Authorization') {
        return 'idor-vul';
      }

      if (category === 'Cross Site Request Forgery') {
        return 'csrf-vul';
      }

      if (vulId.includes('gql')) {
        return 'gql-vul';
      }

      // 其他类型使用 sink-vul 组件
      return 'sink-vul';
    },
    sinkInfo() {
      const { sink } = this.vul;

      if (sink) {
        const { code_snippet, location, ...restSinkInfo } = sink;

        const sinkCode = code_snippet || null;
        const sinkCodeLocation = location || null;

        const info = Object.entries(restSinkInfo).map(([key, value]) => ({
          taint: 'Sink',
          item: KeyMap[key] || key,
          information: value || '-',
        }));

        return {
          sinkCode,
          sinkCodeLocation,
          info,
        };
      }

      return null;
    },
    sourceInfo() {
      const { source } = this.vul;

      if (source) {
        const { code_snippet, location, ...restSourceInfo } = source;

        const sourceCode = code_snippet || null;
        const sourceCodeLocation = location || null;

        const info = Object.entries(restSourceInfo).map(([key, value]) => ({
          taint: 'Source',
          item: KeyMap[key] || key,
          information: value || '-',
        }));

        return {
          sourceCode,
          sourceCodeLocation,
          info,
        };
      }

      return null;
    },
    images() {
      const { vul } = this;

      return vul.images || [];
    },
    language() {
      const { vul } = this;

      return vul.taskLang || vul.lang || LANGUAGE_TYPES.JAVA;
    },
  },
  methods: {
    objectSpanMethod({ rowIndex, columnIndex }) {
      if (columnIndex === 0) {
        if (rowIndex % 4 === 0) {
          return {
            rowspan: 4,
            colspan: 1,
          };
        } else {
          return {
            rowspan: 0,
            colspan: 0,
          };
        }
      }
    },
    copyOxpeckerIp(code) {
      copyText(code);
    },
  },
};
</script>

<style lang="scss">
.detailed-description {
  margin: 20px 20px;
  box-sizing: border-box;

  .images {
    .image-item {
      margin-top: 16px;
      text-align: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  .moduleWrapper {
    margin: 20px 0;
  }

  .description-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #000;
    color: #fff;
    padding: 10px 30px;
    height: 20px;
    border-radius: 5px;
    font-size: 20px;
  }

  .info-container {
    display: flex;
    justify-content: space-between;
    background-color: #f5f5f5;
    margin: 10px 30px;

    .vulLabel {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 5px 20px;
      color: #2b2b2b;
      font-size: 14px;
    }
  }

  .info-box {
    width: 50%;
  }

  .info-box p {
    display: flex;
    margin: 0;
    padding: 0;
    border-bottom: 1px solid #ddd;
  }

  .info-box p:last-child {
    border-bottom: none;
  }

  .info-box strong {
    display: inline-block;
    width: 150px;
    background-color: #2a2a2a;
    color: #fff;
    padding: 5px;
    box-sizing: border-box;
  }

  .section {
    display: flex;
    justify-content: space-between;
    margin: 0px 30px;
    border-bottom: 6px solid #2b2b2b;
    height: 55px;

    button {
      height: 30px;
      line-height: 30px;
      background-color: #fd4d3d;
      padding: 0px 20px;
      margin: 20px 0;
    }

    .el-icon-video-play {
      width: 30px;
      height: 30px;
      position: relative;
      top: 10px;
      right: 10px;
    }
  }

  .vul-section {
    margin: 0px 30px;
  }

  h3 {
    margin-bottom: 16px;
    font-size: 18px;
    font-weight: bold;
  }

  .sections {
    margin: 0px 30px;

    .des-title {
      div {
        margin-top: 20px;

        a {
          color: #2b2b2b;
        }
      }
    }
  }

  .taint-sink {
    margin: 16px 0;

    .el-table th.el-table__cell {
      background-color: #2b2b2b;
      color: #ffffff;
    }
  }

  .code-box {
    margin: 30px 30px;

    .code-title {
      display: flex;
      justify-content: space-between;
      background-color: #2a2a2a;
      color: #ffffff;

      h3 {
        height: 20px;
        position: relative;
        top: -8px;
        left: 20px;
      }

      img {
        width: 20px;
        height: 20px;
        position: relative;
        top: 10px;
        right: 20px;
      }
    }
  }

  .codeWrapper {
    margin: 16px 0 32px;

    .highlighted-line {
      background-color: rgb(149, 29, 29);
    }

    .codeTitle {
      padding: 8px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background-color: #2a2a2a;
      color: #ffffff;
    }

    h3 {
      height: 24px;
      line-height: 24px;
      margin: 0;
    }

    img {
      width: 20px;
      height: 20px;
    }

    .CodeMirror {
      max-height: 500px;
    }
  }

  .no-data {
    position: relative;
    margin: 16px 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 400px;
    font-size: 20px;
    background-color: #000;
    color: #fff;
    border-radius: 6px;
    padding: 20px;

    img {
      margin-top: 50px;
      width: 100px;
      height: 100px;
    }

    span {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 20px;
      color: #fff;
    }
  }

  .paid {
    color: #13ce66 !important;
  }

  .unpaid {
    color: #f56c6c !important;
  }
}

.is-mobile .detailed-description {
  margin: 0;
  font-size: 12px;

  .info-container {
    flex-direction: column;
  }

  .info-box {
    width: 100%;

    p:last-child {
      border-bottom: 1px solid #ddd;
    }
  }

  .no-data {
    height: 220px;

    img {
      margin-top: 20px;
      width: 60px;
      height: 60px;
    }

    span {
      font-size: 14px;
      text-align: center;
    }
  }
}
</style>
